/**
 * Highlights, notes, and bookmarks CRUD operations.
 */

import { generateId } from '../utils';
import { ensureDB, getDB, getByBookId, getById, put, addPendingChange, now } from './helpers';

// =====================================================
// Highlights
// =====================================================

export async function getHighlights(bookId, userId) {
    const highlights = await getByBookId('highlights', bookId, userId);
    return highlights.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
}

export async function createHighlight(userId, data) {
    const highlight = {
        id: data.id || generateId(),
        user_id: userId,
        book_id: data.book_id,
        text: data.text,
        cfi_range: data.cfi_range,
        color: data.color || 'yellow',
        style: data.style || 'background',
        chapter: data.chapter || null,
        created_at: now(),
        updated_at: now(),
        deleted_at: null,
        _pending: true,
    };
    await put('highlights', highlight);
    await addPendingChange('highlights', 'create', highlight);
    return highlight;
}

export async function updateHighlight(id, updates) {
    const highlight = await getById('highlights', id);
    if (!highlight) return null;

    const updated = { ...highlight, ...updates, updated_at: now(), _pending: true };
    await put('highlights', updated);
    await addPendingChange('highlights', 'update', updated);
    return updated;
}

export async function deleteHighlight(id) {
    const highlight = await getById('highlights', id);
    if (!highlight) return;

    const deleted = { ...highlight, deleted_at: now(), updated_at: now(), _pending: true };
    await put('highlights', deleted);
    await addPendingChange('highlights', 'delete', deleted);
}

// =====================================================
// Notes
// =====================================================

export async function getNotes(bookId, userId) {
    const notes = await getByBookId('notes', bookId, userId);
    return notes.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
}

export async function createNote(userId, data) {
    const note = {
        id: data.id || generateId(),
        user_id: userId,
        book_id: data.book_id,
        content: data.content,
        cfi_range: data.cfi_range || null,
        selected_text: data.selected_text || null,
        highlight_id: data.highlight_id || null,
        // parent_note_id: when present, this note is a REPLY to another note in
        // the same book.  Used by NotesWaterfall to render a thread.  Multi-
        // level allowed (a reply may itself be replied to).
        parent_note_id: data.parent_note_id || null,
        chapter: data.chapter || null,
        // Edit counters are server-maintained (trigger notes_on_content_update).
        // Set sane defaults locally so renderers don't get undefined; the next
        // sync pull will overwrite with authoritative values.
        edit_count: 0,
        last_edited_at: null,
        created_at: now(),
        updated_at: now(),
        deleted_at: null,
        _pending: true,
    };
    await put('notes', note);
    await addPendingChange('notes', 'create', note);
    return note;
}

// =====================================================
// Note revisions (read-only on the client)
// =====================================================
//
// Revisions are server-authoritative: the backend AFTER UPDATE OF content
// trigger snapshots OLD content into note_revisions, and `/reader/sync` pulls
// them via `changes.note_revisions`.  The client never WRITES revisions —
// editing a note's content goes through the normal updateNote path; the next
// sync round-trip yields the new revision row.
//
// This getter is the read side: it powers the inline edit-history viewer in
// the Deep Reading panel.  Returned rows are SHALLOWLY soft-delete-aware
// (deleted_at filtered) and ordered oldest → newest, mirroring the backend
// listNoteRevisions endpoint so a fallback fetch can drop in seamlessly.

export async function getNoteRevisions(noteId, userId) {
    if (!noteId || !userId) return [];
    if (!(await ensureDB())) return [];
    const db = getDB();
    if (!db.objectStoreNames.contains('note_revisions')) return [];
    return new Promise((resolve, reject) => {
        const tx = db.transaction('note_revisions', 'readonly');
        const store = tx.objectStore('note_revisions');
        const index = store.index('note_id');
        const req = index.getAll(noteId);
        req.onsuccess = () => {
            const rows = (req.result || [])
                .filter((r) => r.user_id === userId && !r.deleted_at)
                .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            resolve(rows);
        };
        req.onerror = () => reject(req.error);
    });
}

export async function updateNote(id, updates) {
    const note = await getById('notes', id);
    if (!note) return null;

    const updated = { ...note, ...updates, updated_at: now(), _pending: true };
    await put('notes', updated);
    await addPendingChange('notes', 'update', updated);
    return updated;
}

export async function deleteNote(id) {
    const note = await getById('notes', id);
    if (!note) return;

    const deleted = { ...note, deleted_at: now(), updated_at: now(), _pending: true };
    await put('notes', deleted);
    await addPendingChange('notes', 'delete', deleted);
}

// =====================================================
// Bookmarks
// =====================================================

export async function getBookmarks(bookId, userId) {
    const bookmarks = await getByBookId('bookmarks', bookId, userId);
    return bookmarks.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
}

export async function createBookmark(userId, data) {
    const bookmark = {
        id: data.id || generateId(),
        user_id: userId,
        book_id: data.book_id,
        cfi: data.cfi,
        label: data.label || null,
        chapter: data.chapter || null,
        page: data.page || null,
        created_at: now(),
        updated_at: now(),
        deleted_at: null,
        _pending: true,
    };
    await put('bookmarks', bookmark);
    await addPendingChange('bookmarks', 'create', bookmark);
    return bookmark;
}

export async function updateBookmark(id, updates) {
    const bookmark = await getById('bookmarks', id);
    if (!bookmark) return null;

    const updated = { ...bookmark, ...updates, updated_at: now(), _pending: true };
    await put('bookmarks', updated);
    await addPendingChange('bookmarks', 'update', updated);
    return updated;
}

export async function deleteBookmark(id) {
    const bookmark = await getById('bookmarks', id);
    if (!bookmark) return;

    const deleted = { ...bookmark, deleted_at: now(), updated_at: now(), _pending: true };
    await put('bookmarks', deleted);
    await addPendingChange('bookmarks', 'delete', deleted);
}
